import React, {useContext, useEffect, useState, useRef} from "react"
import {breakpoints} from "@next/themes"
import {StyledButton, StyledTypography, CopyContainer, AccordionCopyContainer, ColumnContainer} from "./components"
import {CopyProps} from "./models"
import {Text} from "../text"
import {
    COPY_TYPE,
    SearchBannerContext,
    shouldHideReadMore,
    useWindowSize,
    getSearchBannerComponentTestId,
} from "../utils"
import StructuredBrowsingChips from "../../structuredBrowsingChips"

export const Copy = ({overrideStyles, content, isIntelRecs}: CopyProps) => {
    const size = useWindowSize()
    const {isExpanded, toggleIsExpanded, handleShowChevron} = useContext(SearchBannerContext)
    const [hideReadMore, setHideReadMore] = useState<boolean>()
    const copyRef = useRef<HTMLParagraphElement>(null)
    const {text, components} = content
    const textStyles = {
        lineClamp: overrideStyles?.lineClamp,
        fontSize: overrideStyles?.fontSize,
        fontWeight: overrideStyles?.fontWeight,
        textAlign: overrideStyles?.textAlign,
        textColor: overrideStyles?.textColor,
    }
    const containerStyles = {
        backgroundColor: overrideStyles?.backgroundColor,
        flexDirection: overrideStyles?.flexDirection,
        alignItems: overrideStyles?.alignItems,
    }
    const readMoreStyles = {
        readMoreColor: overrideStyles?.readMoreColor,
        readMoreTextTransform: overrideStyles?.readMoreTextTransform,
    }
    const linkStyles = {
        linkColor: overrideStyles?.linkColor,
        linkHoverColor: overrideStyles?.linkHoverColor,
        linkUnderline: overrideStyles?.linkUnderline,
        linkWeight: overrideStyles?.linkWeight,
    }
    const disableChevron = overrideStyles?.disableChevron ?? false
    useEffect(() => {
        const copyElement = copyRef?.current
        if (!copyElement) return
        // if StructuredBrowsingChips exist, we do not want to hide the read-more, even if the copy is short
        const hideReadMore = shouldHideReadMore(copyElement, isExpanded)
        setHideReadMore(disableChevron ? false : hideReadMore)
    }, [size, isExpanded])
    useEffect(() => {
        // if we are showing the Copy component we should show the chevron
        // but if disableChevron is set in the overrides we should not show the chevron
        handleShowChevron(!disableChevron)
        return () => {
            // when the copy is unmounted remove the chevron
            handleShowChevron(false)
        }
    }, [])
    return (
        <ColumnContainer>
            <CopyContainer
                $containerStyles={containerStyles}
                data-testid={getSearchBannerComponentTestId(isIntelRecs, "copy")}
                $disableChevron={disableChevron}
            >
                <StyledTypography $noWrap={!isExpanded} $textStyles={textStyles} ref={copyRef}>
                    <Text text={text as string} replacements={components} linkStyles={linkStyles} type={COPY_TYPE} />
                </StyledTypography>
                {!hideReadMore && (
                    <StyledButton
                        variant="text"
                        size="small"
                        onClick={toggleIsExpanded}
                        disableRipple
                        $readMoreStyles={readMoreStyles}
                        data-testid={isExpanded ? "search-banner-read-less" : "search-banner-read-more"}
                    >
                        {/* we should probably be getting this from text json */}
                        {isExpanded ? "- Read less" : "+ Read more"}
                    </StyledButton>
                )}
            </CopyContainer>
            <StructuredBrowsingChips show={isExpanded} />
        </ColumnContainer>
    )
}

export const AccordionCopy = ({overrideStyles, content, showAccordionOnDesktop}: CopyProps) => {
    const size = useWindowSize()
    const {isExpanded, handleShowChevron} = useContext(SearchBannerContext)
    const {text, components} = content
    const textStyles = {
        lineClamp: overrideStyles?.lineClamp,
        fontSize: overrideStyles?.fontSize,
        fontWeight: overrideStyles?.fontWeight,
        textAlign: overrideStyles?.textAlign,
        textColor: overrideStyles?.textColor,
    }
    const containerStyles = {
        backgroundColor: overrideStyles?.backgroundColor,
    }
    const linkStyles = {
        linkColor: overrideStyles?.linkColor,
        linkHoverColor: overrideStyles?.linkHoverColor,
        linkUnderline: overrideStyles?.linkUnderline,
        linkWeight: overrideStyles?.linkWeight,
    }

    useEffect(() => {
        if (showAccordionOnDesktop) {
            handleShowChevron(true)
            return
        }

        const isMobile = size?.width !== undefined && size.width <= breakpoints.values.md
        handleShowChevron(isMobile)
    }, [size])

    return (
        <AccordionCopyContainer
            $containerStyles={containerStyles}
            $isExpanded={isExpanded}
            $showAccordionOnDesktop={showAccordionOnDesktop ?? false}
            data-testid="search-banner-accordion-copy"
        >
            <StyledTypography $noWrap={false} $textStyles={textStyles}>
                {isExpanded && (
                    <Text text={text as string} replacements={components} linkStyles={linkStyles} type={COPY_TYPE} />
                )}
            </StyledTypography>
        </AccordionCopyContainer>
    )
}

export default Copy
